/**
 * Swinging stock-race gate. A later character must hold it open, therefore the
 * leaf is built around a real hinge pivot rather than a decorative static gate.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * How far the hinge sits from the asset's origin, in metres.
 *
 * The gate is built hinge-first — the post at local X 0, the leaf sweeping out
 * to +X — because that is the only arrangement in which `leaf.rotation.y` is a
 * hinge rather than a shear. But an asset whose origin is its pivot has a
 * bounding box that is not centred on the origin, and `asset.spec.ts` requires
 * X to be centred within 0.02 m so that a placement is `position.set(x, 0, z)`
 * with no fudge factor. The first draft declared itself an exception to that
 * rule in a comment, and measured 0.815.
 *
 * So the whole gate hangs under a `mount` group shifted back by half its own
 * width. The origin is the bounding-box centre like every other asset, the
 * pivot is a named child like `wheeled-bin`'s lid and `trailer-ramp`'s hinge,
 * and nothing had to be exempted. A room that wants the hinge at a known x
 * asks the group for `mount` rather than doing arithmetic at the call site.
 */
export const RACE_GATE_HINGE_OFFSET = 0.815;
const HINGE_OFFSET = RACE_GATE_HINGE_OFFSET;

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'race-gate';

  const mount = new THREE.Group();
  mount.name = 'mount';
  mount.position.set(-HINGE_OFFSET, 0, 0);
  group.add(mount);

  const brightSteel = surface('metal', 0x9aa2a8);
  const dullSteel = surface('metal', 0x767d82);

  // This static post is deliberately not in the leaf: it is the hinge itself.
  // NEGATIVE leaf.rotation.y sweeps its +X children toward +Z (right-hand rule
  // about +Y takes +X to −Z). This said "positive" until beat 12 turned the leaf
  // and measured it; `RACE_GATE_OPEN_YAW` in `loadingYard.ts` is the consumer.
  const hingePost = part(new THREE.BoxGeometry(0.09, 1.20, 0.09), dullSteel, 'static-hinge-post');
  hingePost.position.set(0, 0.60, 0);
  mount.add(hingePost);

  // Plain box lugs make the physical hinge legible without inventing decorative hardware.
  for (const [y, name] of [[0.28, 'lower-hinge-lug'], [0.96, 'upper-hinge-lug']] as const) {
    const lug = part(new THREE.BoxGeometry(0.10, 0.08, 0.11), dullSteel, name);
    lug.position.set(0.07, y, 0);
    mount.add(lug);
  }

  const leaf = new THREE.Group();
  leaf.name = 'leaf';
  leaf.position.set(0, 0, 0);
  leaf.rotation.y = 0;

  // The frame's outer edges are X=0.06 and X=1.66, giving its specified 1.60 m
  // width while leaving the hinge post visibly outside the moving leaf.
  const frameBars: readonly [readonly [number, number, number], readonly [number, number, number], string][] = [
    [[0.05, 1.10, 0.05], [0.085, 0.55, 0], 'leaf-frame-hinge-upright'],
    [[0.05, 1.10, 0.05], [1.635, 0.55, 0], 'leaf-frame-far-upright'],
    [[1.50, 0.05, 0.05], [0.86, 0.025, 0], 'leaf-frame-bottom-rail'],
    [[1.50, 0.05, 0.05], [0.86, 1.075, 0], 'leaf-frame-top-rail'],
  ];
  for (const [size, position, name] of frameBars) {
    const bar = part(new THREE.BoxGeometry(...size), dullSteel, name);
    bar.position.set(...position);
    leaf.add(bar);
  }

  // The three plain, evenly spaced inner rails span X=0.11 to 1.61. Eight-sided
  // capped pipe preserves the kit's controlled polygon budget without softening
  // the intentionally orthogonal yard silhouette.
  for (const [y, name] of [[0.28, 'bright-inner-rail-low'], [0.55, 'bright-inner-rail-middle'], [0.82, 'bright-inner-rail-high']] as const) {
    const rail = part(new THREE.CylinderGeometry(0.02, 0.02, 1.50, 8), brightSteel, name);
    rail.rotation.z = Math.PI / 2;
    rail.position.set(0.86, y, 0);
    leaf.add(rail);
  }

  // A thin square-section diagonal is the plainest rigidifying brace choice. It
  // joins the centres of opposing 0.05 m frame members, so it actually braces
  // the corners rather than reading as an inexplicably inset decorative slash.
  const brace = part(new THREE.BoxGeometry(Math.sqrt(1.55 ** 2 + 1.05 ** 2), 0.035, 0.035), dullSteel, 'leaf-diagonal-brace');
  brace.position.set(0.86, 0.55, 0);
  brace.rotation.z = Math.atan2(1.05, 1.55);
  leaf.add(brace);

  // A short drop bar and box keeper are intentionally plain service hardware:
  // they identify the far-end closure without implying an unbuilt locking system.
  const latchBar = part(new THREE.BoxGeometry(0.04, 0.22, 0.12), dullSteel, 'leaf-drop-latch-bar');
  latchBar.position.set(1.60, 0.55, 0);
  const latchBracket = part(new THREE.BoxGeometry(0.04, 0.10, 0.12), dullSteel, 'far-end-latch-bracket');
  latchBracket.position.set(1.655, 0.55, 0);
  leaf.add(latchBar, latchBracket);
  mount.add(leaf);

  // The room owns the static catch relationship, so this is deliberately an
  // empty root-level anchor rather than geometry that would rotate with the leaf.
  const latchKeeper = new THREE.Group();
  latchKeeper.name = 'latch-keeper';
  latchKeeper.position.set(1.68, 0, 0);
  mount.add(latchKeeper);

  return group;
}

export const raceGateAsset: AssetDefinition = {
  id: 'race-gate',
  label: 'Swinging stock-race gate',
  description: 'Hinged galvanized stock-race gate with a plain drop latch.',
  footprint: { width: 1.72, height: 1.20, length: 0.12 },
  create: build,
};
